import React, { useEffect, useState } from "react";
import { Search, X, Sparkles, Tag, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { listarStickers } from "../../utils/calibStickers";

const NUEVO = "__nuevo__";

/**
 * Selector de sticker de calibración: uno libre (pre-impreso) o uno nuevo.
 * onConfirm recibe el id del sticker libre, o null para generar uno nuevo.
 */
const AsignarStickerModal = ({
  open,
  onClose,
  onConfirm,
  titulo = "Asignar sticker de calibración",
  textoBoton = "Asignar sticker",
  aviso,
  procesando = false,
}) => {
  const [busqueda, setBusqueda] = useState("");
  const [libres, setLibres] = useState([]);
  const [totalLibres, setTotalLibres] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [seleccion, setSeleccion] = useState("");

  useEffect(() => {
    if (!open) return;
    setSeleccion("");
    setBusqueda("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setCargando(true);
      try {
        const data = await listarStickers({
          estado: "libre",
          q: busqueda || undefined,
          limit: 30,
        });
        setLibres(data.items || []);
        setTotalLibres(data.total || 0);
      } catch {
        setLibres([]);
      } finally {
        setCargando(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [open, busqueda]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{titulo}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Elige el sticker pre-impreso que vas a pegar en el equipo. Su
              número será el número del certificado.{" "}
              <strong className="text-gray-700">
                La asignación es definitiva y no podrá modificarse.
              </strong>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={procesando}
            className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 space-y-3 overflow-y-auto">
          {aviso && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2.5 rounded-lg">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{aviso}</span>
            </div>
          )}

          <label
            className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer transition-colors ${
              seleccion === NUEVO
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:bg-gray-50"
            }`}
          >
            <input
              type="radio"
              name="sticker"
              checked={seleccion === NUEVO}
              onChange={() => setSeleccion(NUEVO)}
            />
            <Sparkles size={16} className="text-blue-600 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-gray-800">
                Generar un sticker nuevo
              </div>
              <div className="text-xs text-gray-500">
                Si no llevas stickers impresos; podrás imprimirlo al terminar.
              </div>
            </div>
          </label>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-gray-600">
                Stickers libres ({totalLibres})
              </span>
            </div>
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar número, ej. 000123"
                className="w-full border rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-1 focus:ring-blue-400 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {libres.map((s) => (
              <label
                key={s._id}
                className={`flex items-center gap-2 border rounded-lg px-2.5 py-2 cursor-pointer text-sm font-mono transition-colors ${
                  seleccion === s._id
                    ? "border-blue-500 bg-blue-50 text-blue-800"
                    : "border-gray-200 hover:bg-gray-50 text-gray-700"
                }`}
              >
                <input
                  type="radio"
                  name="sticker"
                  className="sr-only"
                  checked={seleccion === s._id}
                  onChange={() => setSeleccion(s._id)}
                />
                <Tag size={13} className="flex-shrink-0 opacity-60" />
                {s.numero}
              </label>
            ))}
          </div>
          {!cargando && libres.length === 0 && (
            <p className="text-xs text-gray-500 text-center py-3">
              {busqueda
                ? "Ningún sticker libre coincide con la búsqueda."
                : "No hay stickers libres. Genera uno nuevo o créalos en Configuración › Stickers de Calibración."}
            </p>
          )}
          {cargando && (
            <p className="text-xs text-gray-400 text-center py-3">Cargando…</p>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 px-5 py-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={procesando}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className="bg-green-600 hover:bg-green-700"
            disabled={!seleccion || procesando}
            onClick={() => onConfirm(seleccion === NUEVO ? null : seleccion)}
          >
            {procesando ? "Procesando…" : textoBoton}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AsignarStickerModal;
